import { useElementBoundingClientRectObserver } from 'hooks/useElementSize'
import useHasMounted from 'hooks/useHasMounted'
import isHTMLInputElement from 'lib/dom/isHTMLInputElement'
import { rem } from 'polished'
import type { FormEventHandler, PropsWithChildren } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import type { SegmentedOptionsContextValue } from './contexts/SegmentedOptionsContext'
import SegmentedOptionsContext from './contexts/SegmentedOptionsContext'
import useScrollEndsObserver from 'hooks/useScrollEndsObserver'
import clsx from 'clsx'
import {
  getElementTextDirection,
  getLogicalInlineCenterPosition,
  scrollByLogicalPosition,
} from 'lib/dom/logicalScroll'

const OPTIONS_GROUP_PADDING = 4
const FADED_OVERFLOW_WIDTH = 64

const Container = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  overflow: hidden;
  isolation: isolate; /* Create stacking context */

  &.distribution-hugged {
    display: inline-block;
    max-width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    bottom: 0;
    width: ${rem(FADED_OVERFLOW_WIDTH)};
    opacity: var(--start-fade-opacity, 0);
    transition: opacity 600ms;
    background-image: linear-gradient(to right, ${props => props.theme.palette.neutral.surface.lightBackground} 20%, transparent);
    z-index: 3;
    pointer-events: none;
  }

  &:dir(rtl)::before {
    background-image: linear-gradient(to left, ${props => props.theme.palette.neutral.surface.lightBackground} 20%, transparent);
  }

  &::after {
    content: '';
    position: absolute;
    inset-inline-end: 0;
    top: 0;
    bottom: 0;
    width: ${rem(FADED_OVERFLOW_WIDTH)};
    opacity: var(--end-fade-opacity, 0);
    transition: opacity 600ms;
    background-image: linear-gradient(to left, ${props => props.theme.palette.neutral.surface.lightBackground} 20%, transparent);
    z-index: 3;
    pointer-events: none;
  }

  &:dir(rtl)::after {
    background-image: linear-gradient(to right, ${props => props.theme.palette.neutral.surface.lightBackground} 20%, transparent);
  }

  &.hasReachedHorizontalStart:not(.hasReachedHorizontalEnd) {
    --end-fade-opacity: 1;
  }
  &:not(.hasReachedHorizontalStart).hasReachedHorizontalEnd {
    --start-fade-opacity: 1;
  }
`

const Shadow = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  border-radius: ${props => props.theme.borderRadius.round};
  transition: box-shadow 600ms;

  &:not(.hasReachedHorizontalStart):not(.hasReachedHorizontalEnd) {
    box-shadow: 0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, 1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, 4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset, -0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, -1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, -4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset;
  }
  &.hasReachedHorizontalStart:not(.hasReachedHorizontalEnd) {
    box-shadow: -0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, -1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, -4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset;
  }
  &:dir(rtl).hasReachedHorizontalStart:not(.hasReachedHorizontalEnd) {
    box-shadow: 0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, 1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, 4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset;
  }
  &:not(.hasReachedHorizontalStart).hasReachedHorizontalEnd {
    box-shadow: 0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, 1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, 4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset;
  }
  &:dir(rtl):not(.hasReachedHorizontalStart).hasReachedHorizontalEnd {
    box-shadow: -0.7px 0 1.4px 0 rgba(0, 0, 0, 0.07) inset, -1.9px 0 4px 0 rgba(0, 0, 0, 0.05) inset, -4.5px 0 10px 0 rgba(0, 0, 0, 0.05) inset;
  }
`

const Indicator = styled.div`
  z-index: 1;
  opacity: 0;
  width: 0;
  position: absolute;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.medium};
  border-radius: ${props => props.theme.borderRadius.round};
  transition: opacity 200ms, transform 200ms, height 200ms, width 200ms;
  transform: translate(${rem(OPTIONS_GROUP_PADDING)}, ${rem(OPTIONS_GROUP_PADDING)});
  height: calc(100% - ${rem(OPTIONS_GROUP_PADDING * 2)});
`

const OptionsGroup = styled.div`
  position: relative;
  padding: ${rem(OPTIONS_GROUP_PADDING)};
  display: grid;
  grid-auto-flow: column;
  align-items: stretch;
  gap: ${rem(4)};
  overflow-x: auto;
  overflow-y: hidden;

  &.distribution-filled {
    grid-auto-columns: minmax(max-content, 1fr);
  }

  &.distribution-hugged {
    grid-auto-columns: minmax(max-content, auto);
  }

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`

export type SegmentedOptionsGroupOnValueChangeHandler<T = string> = (value: T) => void

interface Props<T> extends PropsWithChildren, Utils.PropsWithDataAttributes {
  className?: string
  /**
   * Sets the `name` attribute for every `SegmentedOption` child.
   */
  inputsName: string
  onValueChange?: SegmentedOptionsGroupOnValueChangeHandler<T>
  /**
   * - `filled` - the component will have `display: block` and will fill the available width. Options will stretch to equal width, if there's room.
   * - `hugged` - the component will have `display: inline-block` and will only take up the space it needs to. Options will be sized according to their content.
   *
   * In both cases, it will become scrollable if needed. If a design specified `distribution="scroll"`, use `filled`.
   *
   * @default `hugged`
   * */
  distribution?: 'filled' | 'hugged'
}

function SegmentedOptionsGroup<T = string>({
  children,
  inputsName,
  onValueChange,
  distribution = 'hugged',
  className,
  ...rest
}: Props<T>) {
  const fieldSetRef = useRef<HTMLFieldSetElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const attachIndicator = useCallback((element: HTMLInputElement) => {
    if (indicatorRef.current) {
      if (element.type === 'radio') {
        const inputLabel: HTMLLabelElement | undefined = element.labels?.[0]
        if (inputLabel) {
          const rect = inputLabel.getBoundingClientRect()
          indicatorRef.current.style.opacity = '1'
          indicatorRef.current.style.transform = `translate(${inputLabel.offsetLeft}px, ${inputLabel.offsetTop}px)`
          indicatorRef.current.style.height = `${rect.height}px`
          indicatorRef.current.style.width = `${rect.width}px`
        }
      }
    }
  }, [])

  const centreOption = useCallback((element: HTMLLabelElement | undefined) => {
    const group = optionsGroupRef.current
    if (group && element) {
      // Adjust scroll to centre the element
      const textDirection = getElementTextDirection(group)
      const currentCentre = getLogicalInlineCenterPosition(element, textDirection)
      const targetCentre = getLogicalInlineCenterPosition(group, textDirection)
      const scrollPosition = currentCentre - targetCentre
      scrollByLogicalPosition(group, { left: scrollPosition, behavior: 'smooth' })
    }
  }, [])

  const handleChange = useCallback<FormEventHandler<HTMLFieldSetElement>>((event) => {
    if (isHTMLInputElement(event.target)) {
      attachIndicator(event.target)
      centreOption(event.target.labels?.[0])
      onValueChange?.(event.target.value as T)
    }
  }, [attachIndicator, centreOption, onValueChange])

  const hasMounted = useHasMounted()

  const getCheckedInput = useCallback((): HTMLInputElement | undefined => {
    const element = fieldSetRef.current?.querySelector('input[type="radio"]:checked')
    return (!!element && isHTMLInputElement(element)) ? element : undefined
  }, [])

  const reattachIndicator = useCallback(() => {
    if (fieldSetRef.current) {
      const inputElement = getCheckedInput()
      if (inputElement) {
        attachIndicator(inputElement)
      }
    }
  }, [attachIndicator, getCheckedInput])

  useEffect(() => {
    if (hasMounted) {
      reattachIndicator()
    }
    // Needs to run after ever render, in case the selected option changed without
    // the user clicking one of the segments. Therefore, no dependency array.
  })

  useElementBoundingClientRectObserver(fieldSetRef, reattachIndicator)

  const optionsGroupRef = useRef<HTMLDivElement | null>(null)

  const {
    hasReachedHorizontalEnd,
    hasReachedHorizontalStart,
  } = useScrollEndsObserver(optionsGroupRef, children, {
    initiallyHasReachedHorizontalEnd: true,
    initiallyHasReachedHorizontalStart: true,
  })

  const context = useMemo<SegmentedOptionsContextValue>(() => ({ inputsName }), [inputsName])

  // Why the extra `<Container>`?
  // CSS on `fieldset` is slightly unpredictable, therefore adding an extra `<div>` layer,
  // so it can be styled safely.
  return <SegmentedOptionsContext value={context}>
    <Container
      {...rest}
      className={clsx(
        className,
        `distribution-${distribution}`,
        { hasReachedHorizontalStart, hasReachedHorizontalEnd },
      )}
    >
      <Shadow
        className={clsx({
          hasReachedHorizontalStart,
          hasReachedHorizontalEnd,
        })}
      />
      <fieldset ref={fieldSetRef} onChange={handleChange}>
        <OptionsGroup
          ref={optionsGroupRef}
          className={clsx(
            `distribution-${distribution}`,
            { hasReachedHorizontalStart, hasReachedHorizontalEnd },
          )}
        >
          <Indicator role="none" ref={indicatorRef} />
          {children}
        </OptionsGroup>
      </fieldset>
    </Container>
  </SegmentedOptionsContext>
}

export default SegmentedOptionsGroup
